import React, { useEffect, useState, useCallback } from "react";
import MetricCards from '../components/admin/analytics/MetricCards';
import SalesChart from '../components/admin/analytics/SalesChart';
import TopProduct from '../components/admin/analytics/TopProduct';
import BranchSelector from '../components/admin/analytics/BranchSelector';
import EmptyState from '../components/CommonComponents/EmptyState';
import { BarChart3 } from 'lucide-react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const API_URL = `${process.env.REACT_APP_API_URL}/api/admin/orders/analytics`;

const Analytics = () => {
    const [salesRange, setSalesRange] = useState('day'); // 'day', 'week', 'month'
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [branchId, setBranchId] = useState("");
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // Fetch analytics data
    const fetchAnalytics = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token") || sessionStorage.getItem("token");
            const customerId = sessionStorage.getItem("customer_id") || localStorage.getItem("customer_id");

            let url = API_URL;
            const params = [];
            if (customerId) params.push(`customer_id=${customerId}`);
            if (branchId) params.push(`branch_id=${branchId}`);
            params.push(`tzOffset=${new Date().getTimezoneOffset()}`);
            if (params.length) url += `?${params.join("&")}`;

            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = await response.json();
            setAnalytics(data);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [branchId]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    // Normalize backend date (YYYY-MM-DD / ISO) without timezone day-shift.
    const normalizeDate = (dateStr) => {
        if (!dateStr) return null;
        const cleaned = String(dateStr).trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
            const [year, month, day] = cleaned.split("-").map(Number);
            return new Date(year, month - 1, day);
        }
        if (/^\d{4}-\d{2}-\d{2}T/.test(cleaned)) {
            const [year, month, day] = cleaned.slice(0, 10).split("-").map(Number);
            return new Date(year, month - 1, day);
        }
        const parsed = new Date(cleaned);
        return isNaN(parsed) ? null : parsed;
    };

    const toLocalYMD = (date) => {
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };

    const filterByLastDays = (data, days) => {
        if (!Array.isArray(data)) return [];
        const today = new Date();
        const start = new Date(today);
        start.setHours(0, 0, 0, 0);
        start.setDate(start.getDate() - (days - 1));
        const end = new Date(today);
        end.setHours(23, 59, 59, 999);
        return data.filter(d => {
            const dt = normalizeDate(d.date);
            return dt && dt >= start && dt <= end;
        });
    };

    const getStartOfWeek = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = day === 0 ? -6 : 1 - day; // Monday as first day
        d.setDate(d.getDate() + diff);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const getStartOfMonth = (date) => {
        const d = new Date(date.getFullYear(), date.getMonth(), 1);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const filterByRange = (data, start, end) => {
        if (!Array.isArray(data)) return [];
        return data.filter(d => {
            const dt = normalizeDate(d.date);
            return dt && dt >= start && dt <= end;
        });
    };

    const sortByDateAsc = (data) => {
        if (!Array.isArray(data)) return [];
        return [...data].sort((a, b) => {
            const da = normalizeDate(a.date);
            const db = normalizeDate(b.date);
            if (!da || !db) return 0;
            return da - db;
        });
    };

    // Prepare chart data for monthly sales
    const getMonthName = (monthNum) => {
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return months[monthNum - 1] || monthNum;
    };
    const monthlySales = (analytics?.salesByMonth ?? []).map(m => ({
        label: `${getMonthName(m.month)} ${m.year}`,
        totalSales: parseFloat(m.totalSales),
    })).reverse(); // reverse for chronological order
    const monthlyData = monthlySales.map(m => m.totalSales);

    const yearOptions = Array.from(
        new Set((analytics?.salesByMonth ?? []).map(m => Number(m.year)))
    ).filter(Boolean).sort((a, b) => b - a);

    useEffect(() => {
        if (yearOptions.length === 0) return;
        if (!yearOptions.includes(Number(selectedYear))) {
            setSelectedYear(yearOptions[0]);
        }
    }, [yearOptions, selectedYear]);

    const monthlyLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const monthlyDataByYear = monthlyLabels.map((_, idx) => {
        const month = idx + 1;
        const record = (analytics?.salesByMonth ?? []).find(
            m => Number(m.year) === Number(selectedYear) && Number(m.month) === month
        );
        return record ? parseFloat(record.totalSales) : 0;
    });
    const getOverview = () => {
        if (!analytics?.salesByDay) return { total: 0, orders: 0, avg: 0 };
        const localYMD = toLocalYMD(new Date());
        const today = (analytics.salesByDay ?? []).find((d) => {
            const localDate = normalizeDate(d.date);
            return toLocalYMD(localDate) === localYMD;
        });
        const endOfToday = new Date();
        endOfToday.setHours(23, 59, 59, 999);
        const week = filterByLastDays(analytics.salesByDay, 7);
        // Fix: use todayDateLocal for getStartOfMonth argument
        const month = filterByRange(analytics.salesByDay, getStartOfMonth(new Date()), endOfToday);
        const totalSales = (arr) => arr.reduce((sum, d) => sum + parseFloat(d.totalSales), 0);
        const totalOrders = (arr) => arr.reduce((sum, d) => sum + parseInt(d.ordersCount), 0);
        const monthOrders = totalOrders(month);
        return {
            today: today ? parseFloat(today.totalSales) : 0,
            week: totalSales(week),
            month: totalSales(month),
            orders: monthOrders,
            avg: monthOrders > 0 ? totalSales(month) / monthOrders : 0,
        };
    };

    const overview = getOverview();

    // Helper: get filtered sales data for chart
    const getSalesByDayFiltered = () => {
        if (!analytics?.salesByDay) return [];
        if (salesRange === 'week') {
            return sortByDateAsc(filterByLastDays(analytics.salesByDay, 7));
        }
        if (salesRange === 'month') {
            const endOfToday = new Date();
            endOfToday.setHours(23, 59, 59, 999);
            return sortByDateAsc(filterByRange(analytics.salesByDay, getStartOfMonth(endOfToday), endOfToday));
        }
        return sortByDateAsc(analytics.salesByDay);
    };

    const salesByDayFiltered = getSalesByDayFiltered();
    const salesByDayLabels = salesByDayFiltered.map(d => d.date);
    const salesByDayData = salesByDayFiltered.map(d => parseFloat(d.totalSales));
    // ...existing code...

    // Prepare chart data for sales by day

    // Prepare chart data for top products
    const topProductsLabels = (analytics?.topProducts ?? []).map(p => p.product || p.product?.name || p.product_id);
    const topProductsData = (analytics?.topProducts ?? []).map(p => parseInt(p.totalQty));
    const topProductsChart = {
        labels: topProductsLabels,
        datasets: [
            {
                label: 'Top Products',
                data: topProductsData,
                backgroundColor: 'rgba(54, 162, 235, 0.6)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1,
            },
        ],
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="flex items-center justify-between mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Sales Analytics</h1>
                <BranchSelector value={branchId} onChange={setBranchId} />
            </div>
            {error && (
                <EmptyState
                    icon={BarChart3}
                    title="Failed to Load Analytics"
                    message="Check your internet connection and try again."
                    onRetry={fetchAnalytics}
                    retryLabel="Retry"
                />
            )}
            {!error && loading && (
                <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                        {[...Array(4)].map((_, idx) => (
                            <div key={idx} className="bg-white rounded-lg shadow p-4 border-l-4 border-gray-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <div className="h-3 w-24 bg-gray-100 rounded animate-pulse" />
                                        <div className="mt-2 h-6 w-28 bg-gray-100 rounded animate-pulse" />
                                        <div className="mt-2 h-3 w-20 bg-gray-100 rounded animate-pulse" />
                                    </div>
                                    <div className="h-10 w-10 bg-gray-100 rounded-full animate-pulse" />
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex flex-col gap-6 mb-8">
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="h-4 w-40 bg-gray-100 rounded animate-pulse" />
                                <div className="h-8 w-28 bg-gray-100 rounded animate-pulse" />
                            </div>
                            <div className="h-64 bg-gray-100 rounded animate-pulse" />
                        </div>
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="h-4 w-36 bg-gray-100 rounded animate-pulse" />
                                <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
                            </div>
                            <div className="h-64 bg-gray-100 rounded animate-pulse" />
                        </div>
                    </div>
                </div>
            )}
            {!error && !loading && analytics && (
                <>
                    {/* Metrics Card Row */}
                    <MetricCards overview={overview} />

                    {/* Stacked Full-Width Graphs: Sales and Top Products */}
                    <div className="flex flex-col gap-6 mb-8">
                        <SalesChart
                            salesRange={salesRange}
                            onSalesRangeChange={setSalesRange}
                            monthlyData={monthlyData}
                            monthlyLabels={monthlyLabels}
                            monthlyDataByYear={monthlyDataByYear}
                            salesByDayLabels={salesByDayLabels}
                            salesByDayData={salesByDayData}
                            analytics={analytics}
                            selectedYear={selectedYear}
                            onYearChange={setSelectedYear}
                            yearOptions={yearOptions}
                        />
                        <TopProduct topProductsChart={topProductsChart} />
                    </div>
                </>
            )}
        </div>
    );
};

export default Analytics;
